import { getCookie, setCookie } from "@/lib/cookieUtils";
import {
  FBC_COOKIE_NAME,
  FBC_PARAM,
  FBCLID_PARAM,
  FBP_COOKIE_NAME,
  FBP_PARAM,
  META_ATTRIBUTION_STORAGE_KEY,
  META_COOKIE_MAX_AGE_SECONDS,
  META_PIXEL_SCRIPT_URL,
  META_PIXEL_TRACKED_PATHS,
  META_SOURCE_REGEX,
} from "./metaPixel.constants";
import type { MetaAttributionData } from "./metaPixel.types";

const isBrowser = () => typeof window !== "undefined";

export const formatFbcCookie = (
  fbclid: string,
  timestamp: number = Date.now()
): string => `fb.1.${timestamp}.${fbclid}`;

export const parseFbclidFromFbc = (fbc: string): string | undefined => {
  const parts = fbc.split(".");
  // Standard format: fb.1.<timestamp>.<fbclid>
  if (parts.length >= 4 && parts[0] === "fb") {
    return parts.slice(3).join(".");
  }
  return undefined;
};

export const normalizePathname = (pathname: string): string => {
  const trimmed = pathname.trim();
  if (trimmed.length > 1 && trimmed.endsWith("/")) {
    return trimmed.slice(0, -1);
  }
  return trimmed;
};

export const isMetaPixelTrackedPath = (pathname: string): boolean => {
  const normalized = normalizePathname(pathname);
  return (META_PIXEL_TRACKED_PATHS as readonly string[]).includes(normalized);
};

export const getPersistedMetaAttribution = (): MetaAttributionData | null => {
  if (!isBrowser()) return null;

  try {
    const raw =
      window.localStorage.getItem(META_ATTRIBUTION_STORAGE_KEY) ||
      getCookie(META_ATTRIBUTION_STORAGE_KEY);

    if (!raw) return null;

    const parsed = JSON.parse(raw) as MetaAttributionData;
    if (!parsed || typeof parsed !== "object") return null;

    const ageMs = Date.now() - (parsed.detectedAt || 0);
    const maxAgeMs = META_COOKIE_MAX_AGE_SECONDS * 1000;
    if (ageMs > maxAgeMs) {
      clearPersistedMetaAttribution();
      return null;
    }

    return parsed;
  } catch {
    clearPersistedMetaAttribution();
    return null;
  }
};

export const clearPersistedMetaAttribution = (): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(META_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

export const persistMetaAttribution = (data: MetaAttributionData): void => {
  if (!isBrowser()) return;

  const fbcValue =
    data.fbc || (data.fbclid ? formatFbcCookie(data.fbclid, data.detectedAt) : undefined);

  if (fbcValue) {
    setCookie(FBC_COOKIE_NAME, fbcValue, META_COOKIE_MAX_AGE_SECONDS);
  }

  if (data.fbp) {
    setCookie(FBP_COOKIE_NAME, data.fbp, META_COOKIE_MAX_AGE_SECONDS);
  }

  const updatedData: MetaAttributionData = {
    ...data,
    fbc: fbcValue || data.fbc,
  };

  const stringified = JSON.stringify(updatedData);

  try {
    window.localStorage.setItem(META_ATTRIBUTION_STORAGE_KEY, stringified);
  } catch {
    // Fall back to cookie only if localStorage is unavailable
  }

  setCookie(META_ATTRIBUTION_STORAGE_KEY, stringified, META_COOKIE_MAX_AGE_SECONDS);
};

export const detectMetaAttribution = (
  searchParams?: URLSearchParams | null
): MetaAttributionData | null => {
  const urlFbclid = searchParams?.get(FBCLID_PARAM) || undefined;
  const urlFbc = searchParams?.get(FBC_PARAM) || undefined;
  const urlFbp = searchParams?.get(FBP_PARAM) || undefined;
  const utmSource = searchParams?.get("utm_source") || undefined;
  const utmMedium = searchParams?.get("utm_medium") || undefined;
  const utmCampaign = searchParams?.get("utm_campaign") || undefined;

  const cookieFbc = getCookie(FBC_COOKIE_NAME) || undefined;
  const cookieFbp = getCookie(FBP_COOKIE_NAME) || undefined;
  const persisted = getPersistedMetaAttribution();

  const isMetaSource = utmSource ? META_SOURCE_REGEX.test(utmSource) : false;
  const isMetaMedium = utmMedium ? META_SOURCE_REGEX.test(utmMedium) : false;

  const resolvedFbclid =
    urlFbclid ||
    (urlFbc ? parseFbclidFromFbc(urlFbc) : undefined) ||
    (cookieFbc ? parseFbclidFromFbc(cookieFbc) : undefined) ||
    persisted?.fbclid;

  const resolvedFbc =
    urlFbc ||
    cookieFbc ||
    persisted?.fbc ||
    (resolvedFbclid ? formatFbcCookie(resolvedFbclid) : undefined);

  const resolvedFbp = urlFbp || cookieFbp || persisted?.fbp;

  const hasMetaAttribution = Boolean(
    resolvedFbclid ||
    resolvedFbc ||
    resolvedFbp ||
    isMetaSource ||
    isMetaMedium ||
    persisted
  );

  if (!hasMetaAttribution) {
    return null;
  }

  return {
    fbclid: resolvedFbclid,
    fbc: resolvedFbc,
    fbp: resolvedFbp,
    utmSource: utmSource || persisted?.utmSource,
    utmMedium: utmMedium || persisted?.utmMedium,
    utmCampaign: utmCampaign || persisted?.utmCampaign,
    detectedAt: persisted?.detectedAt || Date.now(),
  };
};

export const ensureMetaPixelInitialized = (pixelId?: string): boolean => {
  if (!isBrowser()) return false;

  const id =
    pixelId ||
    process.env.NEXT_PUBLIC_META_PIXEL_ID ||
    process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;

  if (!window.fbq) {
    /* eslint-disable */
    (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else if (b.head) {
        b.head.appendChild(t);
      }
    })(window, document, "script", META_PIXEL_SCRIPT_URL);
    /* eslint-enable */
  }

  if (id && window.fbq) {
    if (!window.__metaPixelInitializedIds) {
      window.__metaPixelInitializedIds = new Set<string>();
    }
    if (!window.__metaPixelInitializedIds.has(id)) {
      window.fbq("init", id);
      window.__metaPixelInitializedIds.add(id);
    }
  }

  return true;
};

export const trackMetaPageView = (): boolean => {
  if (!isBrowser() || !window.fbq) return false;
  window.fbq("track", "PageView");
  return true;
};
